import { useFieldArray, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useCreateMenuItemMutation, useUpdateMenuItemMutation } from "../../../../../../API/authApi";
import { ICreateMenuItemForm } from "../../../../../../Interfaces/formValue-interfaces";
import { CreateFormTypes, ImageTypes, MeasureTypes, TimeUnitTypes } from "../../../../../../Interfaces/componentTypes";
import { ICreateMenuItem, IMenuItemOption, IUpdateMenuItem } from "../../../../../../Interfaces/request-interfaces";
import useUploadFile from "../../../../../../Components/FileUploader/fileUploader-hooks";
import { ICreateMenuItemDialogProps } from "./createMenuDialog";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";
import { useToastify } from "../../../../../../Hooks/hooks";

const useCreateMenuItem = (props: ICreateMenuItemDialogProps) => {
    const { categoryId, companyId, onClose, cb } = props;
    const { t } = useLanguagesHooks();
    const [activeForm, setActiveForm] = useState<CreateFormTypes>(CreateFormTypes.CREATE);
    const [create, { data: menuItemResponce, isLoading: createLoading, isSuccess: createSuccess, isError: createError }] = useCreateMenuItemMutation();
    const [updateItem, { isLoading: updateLoading, isSuccess: updateSuccess, isError: updateError }] = useUpdateMenuItemMutation();
    const [isFormChanged, setIsFormChanged] = useState(false);
    const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.CATEGORY);
    const { showToastSuccess, showToastError } = useToastify();
    const [options, setOptions] = useState<[] | IMenuItemOption[]>()

    const {
        register,
        control,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useForm<ICreateMenuItemForm>({
        mode: 'all',
        defaultValues: {
            option: [{ measure_type: undefined, measure_value: '', price: undefined, is_active: true }]
        },
    });
    const watchedFields = watch();
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const createValidation = isFormValid && imageUrl && getValues('name');
    const { fields, append, remove, update, } = useFieldArray({
        control,
        name: "option"
    });

    const validateForm = (): boolean => {
        const formValues = getValues();
        const { name, option } = formValues;
        if (!name || name.trim() === "") return false;
        for (let i = 0; i < option.length; i++) {
            const { measure_type, price } = option[i];
            if (!measure_type || !price || price.trim() === "") return false;
        }

        return true;
    };

    useEffect(() => {
        const subscription = watch(() => {
            const isValid = validateForm();
            setIsFormValid(isValid);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const addOption = () => {
        append({ measure_type: undefined, measure_value: "", price: undefined, is_active: false })
    };

    const deleteOption = (index: number) => {
        remove(index)
    };

    const onCloseUpdateDialog = () => {
        cb();
        onClose()
    };

    useEffect(() => {
        if (menuItemResponce) {
            const initialValues = {
                name_hy: menuItemResponce.name_hy || "",
                name_en: menuItemResponce.name_en || "",
                name_ru: menuItemResponce.name_ru || "",
                description_hy: menuItemResponce.description_hy,
                description_en: menuItemResponce.description_en,
                description_ru: menuItemResponce.description_ru,
            };

            const relevantWatchedFields = {
                name_hy: watchedFields.name_hy,
                name_en: watchedFields.name_en,
                name_ru: watchedFields.name_ru,
                description_hy: watchedFields.description_hy,
                description_en: watchedFields.description_en,
                description_ru: watchedFields.description_ru,
            };

            const hasChanged = Object.keys(initialValues).some(
                (key) => relevantWatchedFields[key as keyof typeof initialValues] !== initialValues[key as keyof typeof initialValues]
            );
            setIsFormChanged(hasChanged);
            // cb();
        }
    }, [watchedFields, menuItemResponce]);


    useEffect(() => {
        if (createSuccess && menuItemResponce) {
            setValue("name_en", menuItemResponce.name_en);
            setValue("name_hy", menuItemResponce.name_hy);
            setValue("name_ru", menuItemResponce.name_ru);
            setValue("description_en", menuItemResponce.description_en);
            setValue("description_hy", menuItemResponce.description_hy);
            setValue("description_ru", menuItemResponce.description_ru);
            if (dialogRef.current) {
                dialogRef!.current.scrollTop = 0;
            }
            setActiveForm(CreateFormTypes.SAVE);
            cb();
        } else if (createError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [createSuccess, menuItemResponce, createError]);

    useEffect(() => {
        if (updateSuccess) {
            showToastSuccess(t('Toast.Success.Common'));
            cb();
            onCloseUpdateDialog()
        } else if (updateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [updateSuccess, updateError])

    const onCreate = () => {
        const options: IMenuItemOption[] = fields.map((option) => {
            return {
                measure_type: option.measure_type?.id as MeasureTypes,
                measure_value: Number(option.measure_value || 1),
                price: option.price!,
                package_cost: option.package_cost!,
                is_active: option.is_active
            };
        })
        setOptions(options);
        const payload: ICreateMenuItem = {
            name: getValues('name')!,
            description: getValues('description')!,
            image: ImageResponce?.src!,
            option: options,
            cooking_time: getValues('cooking_time')!,
            cooking_time_unit: getValues('cooking_time_unit')?.id as TimeUnitTypes,
            category_id: categoryId,
            company_id: companyId
        };
        create(payload)
    };

    const onUpdate = () => {
        const payload: IUpdateMenuItem = {
            category_id: categoryId,
            company_id: companyId,
            menu_item_id: menuItemResponce?.id!,
            name_en: getValues().name_en,
            name_ru: getValues().name_ru,
            name_hy: getValues().name_hy,
            description_en: getValues().description_en,
            description_ru: getValues().description_ru,
            description_hy: getValues().description_hy,
            option: options
        };
        updateItem(payload)
    };

    return {
        fields,
        errors,
        control,
        imageUrl,
        dialogRef,
        activeForm,
        imageLoading,
        isFormChanged,
        updateLoading,
        createLoading,
        menuItemResponce,
        createValidation,
        update,
        onUpdate,
        setValue,
        register,
        onCreate,
        addOption,
        getValues,
        deleteOption,
        handleFileInputChange,
    }
};

export default useCreateMenuItem